// components/common/SEOHead.tsx
// ---------------------------------------------------------------------------
// Shared SEO meta-tag component using react-helmet-async.
// Injects <title>, <meta description>, Open Graph, Twitter Card, and
// canonical URL into the document <head> for each public page.
//
// Usage:
//   <SEOHead
//     title="Our Team"
//     description="Meet the co-founders of Athena Tech."
//     path="/team"
//   />
// ---------------------------------------------------------------------------

import { Helmet } from "react-helmet-async";

const SITE_NAME = "Athena Tech";
const BASE_URL = "https://athenatechai.com";
const DEFAULT_OG_IMAGE = `${BASE_URL}/ATHENA_White.png`;

interface SEOHeadProps {
  /** Page title (will be suffixed with "| Athena Tech") */
  title: string;
  /** Meta description (max ~155 chars recommended) */
  description: string;
  /** Canonical path, e.g. "/team" (base URL prepended automatically) */
  path?: string;
  /** Open Graph image URL (absolute). Falls back to default logo. */
  ogImage?: string;
  /** Override the full title (skip the "| Athena Tech" suffix) */
  fullTitle?: boolean;
}

export default function SEOHead({
  title,
  description,
  path = "/",
  ogImage = DEFAULT_OG_IMAGE,
  fullTitle = false,
}: SEOHeadProps) {
  const formattedTitle = fullTitle ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = `${BASE_URL}${path}`;

  return (
    <Helmet>
      {/* Primary */}
      <title>{formattedTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={formattedTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={formattedTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta
        name="keywords"
        content="oncology data platform, clinical intelligence platform, agentic healthcare, cancer data extraction, GDPR compliant, molecular genetics, patient data analytics, natural language query, cohort analysis, Hospital Clinic Barcelona, OncoCraft, Athena Tech"
      />
    </Helmet>
  );
}
